import * as React from "react";
import {
  Box,
  HStack,
  Text,
} from "@chakra-ui/react";
import { CircleAlert, Info, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { CohortListFilter, CohortPreviewStats } from "../../../../utils/cohortTradesResolve";
import { useReportsDetailColors } from "../reportsDetailTheme";
import {
  TRADE_REVIEW_ALERT_CLASS,
  TRADE_REVIEW_ALERT_DESC_CLASS,
} from "../../TradeLog/components/TradeReview/tradeReviewAlertUi";

function formatMoney(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatPct(value: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}%`;
}

function CohortPreviewMetricsLine(props: {
  stats: CohortPreviewStats;
  reportTradeCount: number;
}): React.ReactElement {
  const { stats, reportTradeCount } = props;
  return (
    <div
      data-market-cohort-preview-metrics=""
      className="mb-3 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 font-[family-name:var(--font-primary)] text-sm font-normal leading-snug text-foreground"
    >
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="text-muted-foreground dark:text-sidebar-muted-foreground">In Cohort:</span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">{stats.inCohort}</span>
        <span className="text-muted-foreground dark:text-sidebar-muted-foreground">
          of {reportTradeCount} report trades
        </span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="text-[color:var(--tessera-pnl-positive)]">{stats.wins}W</span>
        <span className="text-[color:var(--brand-glacial-red)]">{stats.losses}L</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
        <span>{formatPct(stats.winRate)}</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span
          className={cn(
            stats.netPnl >= 0
              ? "text-[color:var(--tessera-pnl-positive)]"
              : "text-[color:var(--brand-glacial-red)]"
          )}
        >
          PnL {formatMoney(stats.netPnl)}
        </span>
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Longs:</span>
        <span>{stats.longs.count}</span>
        {stats.longs.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.longs.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.longs.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.longs.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.longs.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.longs.netPnl)}
            </span>
          </>
        ) : null}
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Shorts:</span>
        <span>{stats.shorts.count}</span>
        {stats.shorts.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.shorts.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.shorts.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.shorts.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.shorts.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.shorts.netPnl)}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

const FILTER_OPTIONS: Array<{ value: CohortListFilter; label: string }> = [
  { value: "cohort", label: "Cohort only" },
  { value: "winners", label: "Winners" },
  { value: "losers", label: "Losers" },
  { value: "all", label: "All" },
];

function isCohortListFilter(value: string): value is CohortListFilter {
  return (
    value === "cohort" ||
    value === "winners" ||
    value === "losers" ||
    value === "all"
  );
}

export type MarketCohortPreviewSummaryProps = {
  label: string;
  stats: CohortPreviewStats;
  reportTradeCount: number;
  expectedSampleSize?: number;
  resolvedTotal: number;
  /**
   * How many resolved cohort IDs exist in the report's loaded trades.
   * Independent of Winners/Losers tab filtering (which only affects `stats`).
   */
  matchedInReportCount: number;
  listFilter: CohortListFilter;
  onListFilterChange: (filter: CohortListFilter) => void;
  onClear: () => void;
  loading?: boolean;
  error?: string;
  isEmpty?: boolean;
};

export function MarketCohortPreviewSummary(props: MarketCohortPreviewSummaryProps): React.ReactElement {
  const colors = useReportsDetailColors();
  const tableSample =
    typeof props.expectedSampleSize === "number" && Number.isFinite(props.expectedSampleSize)
      ? props.expectedSampleSize
      : null;
  const matchedInReport = props.matchedInReportCount;
  const resolvedIds = Math.max(props.resolvedTotal, matchedInReport);
  /** Table sample and matched report trades agree; preview list is complete even if resolve returned extras. */
  const matchedEqualsTableSample =
    tableSample != null && matchedInReport > 0 && matchedInReport === tableSample;
  const unmatchedResolvedIds = Math.max(0, resolvedIds - matchedInReport);

  const showMismatch =
    tableSample != null &&
    props.resolvedTotal > 0 &&
    props.resolvedTotal !== tableSample &&
    !matchedEqualsTableSample;

  const showIdMismatch =
    props.resolvedTotal > 0 && matchedInReport === 0 && !props.loading && !props.error && !props.isEmpty;

  const showPartialIdMismatch =
    unmatchedResolvedIds > 0 &&
    matchedInReport > 0 &&
    !matchedEqualsTableSample &&
    !props.loading &&
    !props.error &&
    !props.isEmpty;

  return (
    <Box mb={3}>
      <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={3} mb={3}>
        <Box>
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily="'Urbanist', sans-serif"
            lineHeight="1.2"
          >
            Preview trades
          </Text>
          {props.label.trim() ? (
            <Text fontSize="sm" color={colors.subtleText} mt={1} fontFamily="'Urbanist', sans-serif">
              {props.label.trim()}
            </Text>
          ) : null}
        </Box>
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-9 shrink-0 font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal"
          onClick={props.onClear}
        >
          Clear preview
        </Button>
      </HStack>

      {props.loading ? (
        <Text fontSize="sm" color={colors.subtleText} fontFamily="'Urbanist', sans-serif">
          Resolving trades for this segment…
        </Text>
      ) : null}

      {props.error ? (
        <Alert variant="error" className={TRADE_REVIEW_ALERT_CLASS}>
          <CircleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>{props.error}</AlertDescription>
        </Alert>
      ) : null}

      {props.isEmpty ? (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            No trades matched this segment.
          </AlertDescription>
        </Alert>
      ) : null}

      {!props.loading && !props.error && !props.isEmpty ? (
        <>
          <CohortPreviewMetricsLine stats={props.stats} reportTradeCount={props.reportTradeCount} />

          {showIdMismatch ? (
            <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
              <TriangleAlert aria-hidden />
              <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
                Resolved {props.resolvedTotal} trade IDs for this segment, but none match the trades loaded for this
                report. The preview list cannot highlight them. This is usually an ID mismatch between resolve and the
                report snapshot.
              </AlertDescription>
            </Alert>
          ) : null}

          {showPartialIdMismatch ? (
            <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
              <TriangleAlert aria-hidden />
              <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
                Only {matchedInReport} of {resolvedIds} resolved trade IDs are in this report’s loaded trades
                {tableSample != null ? ` (table sample ${tableSample})` : ""}. The preview list uses the{" "}
                {matchedInReport} that match. The rest are not in the report snapshot.
              </AlertDescription>
            </Alert>
          ) : null}

          {showMismatch ? (
            <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
              <TriangleAlert aria-hidden />
              <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
                This row’s sample is {tableSample}, but preview rematch found {props.resolvedTotal}{" "}
                trade{props.resolvedTotal === 1 ? "" : "s"} ({matchedInReport} in this report). Preview
                only includes trades that rematch the selected row’s gate — regenerate the report to
                pin exact sample trade IDs.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="w-full overflow-x-auto overflow-y-hidden">
            <Tabs
              data-trade-log-tabs=""
              data-market-cohort-preview-filter-tabs=""
              value={props.listFilter}
              onValueChange={(next) => {
                if (isCohortListFilter(next)) {
                  props.onListFilterChange(next);
                }
              }}
              className="w-fit"
            >
              <TabsList className="min-w-max">
                {FILTER_OPTIONS.map((opt) => (
                  <TabsTrigger
                    key={opt.value}
                    value={opt.value}
                    className="font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal"
                  >
                    {opt.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </>
      ) : null}
    </Box>
  );
}
